import * as React from "react";
import { RefreshCw } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { CursorPage, CursorPageArgs } from "../../../ui/components/Table/hooks/useInfiniteListQuery";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { useWebSocket } from "../../hooks/useWebSocket";
import {
  commandChannel,
  fetchCommandExecutions,
  type CommandExecution,
  type CommandExecutionSource,
  type CommandExecutionStatus,
} from "../../lib/api/commands";
import { CommandExecutionModal } from "../commands/CommandExecutionModal";
import { CommandStatusPill, commandSourceLabel, formatCommandTime } from "../commands/CommandPills";
import { createProductEntityFilter } from "./historyEntityFilters";
import * as s from "../commands/commands.css";
import * as pageStyles from "../users/UsersListPage.css";

const STATUSES: CommandExecutionStatus[] = ["sent", "succeeded", "failed", "timed_out", "rejected"];
const SOURCES: CommandExecutionSource[] = ["pro-web", "mobile", "api", "smart-web-observed"];

function asStrings(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.length > 0 ? value.map(String) : undefined;
}

function durationLabel(row: CommandExecution): string {
  if (!row.sentAt || !row.respondedAt) return "—";
  const ms = new Date(row.respondedAt).getTime() - new Date(row.sentAt).getTime();
  return ms >= 0 ? `${(ms / 1000).toFixed(1)}s` : "—";
}

/** Keeps the open execution current while it is still waiting for the device. */
function useLiveExecution(initial: CommandExecution | null): CommandExecution | null {
  const { subscribe, unsubscribe } = useWebSocket();
  const [live, setLive] = React.useState(initial);
  React.useEffect(() => setLive(initial), [initial]);
  React.useEffect(() => {
    if (!initial || initial.status !== "sent") return;
    const topic = commandChannel(initial.productId);
    const handler = (msg: { type: string; payload?: unknown }) => {
      const row = msg.payload as CommandExecution | undefined;
      if (msg.type === "command.updated" && row?.id === initial.id) {
        setLive((prev) => ({ ...row, productName: prev?.productName ?? initial.productName }));
      }
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [initial, subscribe, unsubscribe]);
  return live;
}

export function CommandHistoryPage() {
  const { accessToken, user } = useAuth();
  const { t } = useI18n();
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);
  const [selected, setSelected] = React.useState<CommandExecution | null>(null);
  const [refreshToken, setRefreshToken] = React.useState(0);
  const live = useLiveExecution(selected);

  const columns = React.useMemo<ColumnDefinition<CommandExecution>[]>(() => {
    const cols: ColumnDefinition<CommandExecution>[] = [
      {
        id: "createdAt",
        label: t("pro.commands.history.col.time"),
        pinnedLeft: true,
        render: (row) => <span className={s.subtle}>{formatCommandTime(row.createdAt)}</span>,
      },
      {
        id: "productName",
        label: t("pro.commands.history.col.product"),
        render: (row) => row.productName ?? "—",
      },
      {
        id: "command",
        label: t("pro.commands.history.col.command"),
        render: (row) => (
          <div>
            <div>{row.displayName ?? row.commandKey}</div>
            <div className={s.subtle}>{row.commandKey}</div>
          </div>
        ),
      },
      {
        id: "status",
        label: t("pro.commands.history.col.status"),
        filterType: "checkbox",
        filterOptions: STATUSES.map((value) => ({ value, label: t(`pro.commands.status.${value}`) })),
        render: (row) => <CommandStatusPill status={row.status} phase={row.phase} />,
      },
      {
        id: "source",
        label: t("pro.commands.history.col.source"),
        filterType: "checkbox",
        filterOptions: SOURCES.map((value) => ({ value, label: commandSourceLabel(t, value) })),
        render: (row) => commandSourceLabel(t, row.source),
      },
      {
        id: "actorName",
        label: t("pro.commands.history.col.sentBy"),
        render: (row) => row.actorName ?? "—",
      },
      {
        id: "duration",
        label: t("pro.commands.history.col.duration"),
        render: (row) => <span className={s.subtle}>{durationLabel(row)}</span>,
      },
    ];
    if (token) {
      cols.push({
        id: "products",
        label: t("pro.commands.history.col.product"),
        filterType: "entity",
        filterKey: "productIds",
        defaultHidden: true,
        entityFilter: createProductEntityFilter(token),
        render: () => null,
      });
    }
    return cols;
  }, [t, token]);

  const fetchPage = React.useCallback(
    async (args: CursorPageArgs): Promise<CursorPage<CommandExecution>> => {
      if (!token) return { data: [], nextCursor: null, hasMore: false };
      const filters = (args.filters ?? {}) as Record<string, unknown>;
      const page = await fetchCommandExecutions(token, {
        limit: args.limit,
        cursor: args.cursor,
        q: args.search,
        status: asStrings(filters.status),
        source: asStrings(filters.source),
        productId: asStrings(filters.productIds)?.join(","),
        from: typeof filters.timeRangeStart === "string" ? filters.timeRangeStart : undefined,
        to: typeof filters.timeRangeEnd === "string" ? filters.timeRangeEnd : undefined,
      });
      return { data: page.data, nextCursor: page.nextCursor, hasMore: Boolean(page.nextCursor) };
    },
    [token],
  );

  const cursorFeed = React.useMemo(() => ({ fetchPage }), [fetchPage]);

  if (!token) {
    return (
      <div className={pageStyles.page}>
        <Callout variant="error">{t("pro.system.history.signInRequired")}</Callout>
      </div>
    );
  }

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <div>
          <Text level="h1" color="text" weight="bold">
            {t("pro.commands.history.title")}
          </Text>
          <span className={s.muted}>{t("pro.commands.history.subtitle")}</span>
        </div>
        <Button
          type="button"
          color="neutral"
          variant="outline"
          iconLeft={<RefreshCw size={14} />}
          onClick={() => setRefreshToken((n) => n + 1)}
        >
          {t("pro.commands.history.refresh")}
        </Button>
      </div>

      <div className={pageStyles.tableHost}>
        <ListView<CommandExecution>
          listId="system-command-history"
          userId={user?.id}
          columns={columns}
          fetchList={async () => ({ data: [], total: 0 })}
          cursorFeed={cursorFeed}
          enabledViews={["table"]}
          defaultPageSize={50}
          refreshToken={refreshToken}
          searchPlaceholder={t("pro.commands.history.search")}
          emptyTitle={t("pro.commands.history.emptyTitle")}
          emptySubtitle={t("pro.commands.history.emptySubtitle")}
          timeRangeFilter={{ enabled: true, label: t("pro.commands.history.dateRange") }}
          onRowClick={setSelected}
          fillContainer
        />
      </div>

      <CommandExecutionModal execution={live} onClose={() => setSelected(null)} showProduct />
    </div>
  );
}
